/**
 * Bounded retry helpers shared by both fetch paths (axios + RTK Query).
 *
 * When the ES concurrency budget is enforced, a saturated `api`/`ws` tier sheds
 * a request as `HTTP 503 + Retry-After`. These helpers let the client honor that
 * `Retry-After` value (plus jitter) and retry idempotent reads a bounded number
 * of times so the shed degrades gracefully instead of surfacing an immediate
 * error. See MKT-293.
 */

/** Status the server uses to shed a request from a saturated tier. */
export const RETRY_STATUS = 503;

/** HTTP 429 Too Many Requests — used for rate-limit detection. */
export const RATE_LIMIT_STATUS = 429;

/**
 * 429s with Retry-After at or below this threshold are silently retried in the
 * retry layer. Above this threshold (or when Retry-After is missing), the error
 * propagates to the token handling layer which shows the user-facing toast.
 */
export const TOAST_THRESHOLD_MS = 10_000;

/** Hard cap on retries so we never loop endlessly (1 original + up to 2 retries). */
export const MAX_RETRIES = 2;

/** Total-time budget across the whole retry sequence — abort past this even on a bad `Retry-After`. */
export const TOTAL_RETRY_BUDGET_MS = 15_000;

/**
 * Fallback delay when the server omits/garbles `Retry-After`. Mirrors the
 * backend `ES_BUDGET_RETRY_AFTER_S` default (2s) so the two stay in step.
 */
export const DEFAULT_RETRY_AFTER_MS = 2_000;

/** Clamp a single `Retry-After` so one absurd value can't force a very long sleep. */
export const MAX_RETRY_AFTER_MS = 10_000;

/** Max random jitter added to each delay so shed clients don't retry in lockstep. */
export const MAX_JITTER_MS = 500;

/** Methods safe to auto-retry. Non-idempotent requests (mutations) are never retried. */
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Whether a request method is idempotent and therefore safe to auto-retry.
 * A missing method defaults to GET (both fetch and axios default to GET).
 */
export const isIdempotentMethod = (method?: string | null): boolean =>
  IDEMPOTENT_METHODS.has((method ?? 'GET').toUpperCase());

/**
 * Parse an HTTP `Retry-After` header into milliseconds. Supports both the
 * delta-seconds form (`"2"`) and the HTTP-date form. Returns `null` when the
 * value is absent or unparseable so callers can fall back to a default.
 */
export const parseRetryAfterMs = (value?: string | null): number | null => {
  if (value == null) return null;

  const trimmed = String(value).trim();
  if (trimmed === '') return null;

  // delta-seconds
  if (/^\d+$/.test(trimmed)) {
    return Number(trimmed) * 1000;
  }

  // HTTP-date
  const dateMs = Date.parse(trimmed);
  if (!Number.isNaN(dateMs)) {
    const delta = dateMs - Date.now();
    return delta > 0 ? delta : 0;
  }

  return null;
};

/**
 * Compute how long to wait before the next retry: honor the server's
 * `Retry-After` (clamped), add jitter, and clamp to the remaining total-time
 * budget. Returns `null` when there is no budget left to retry — the caller
 * should give up and surface the error.
 */
export const computeRetryDelayMs = (
  retryAfterHeader: string | null | undefined,
  elapsedMs: number
): number | null => {
  const remainingBudget = TOTAL_RETRY_BUDGET_MS - elapsedMs;
  if (remainingBudget <= 0) return null;

  const parsed = parseRetryAfterMs(retryAfterHeader);
  const base =
    parsed == null
      ? DEFAULT_RETRY_AFTER_MS
      : Math.min(parsed, MAX_RETRY_AFTER_MS);
  const jitter = Math.random() * MAX_JITTER_MS;

  // Never sleep past what's left of the total budget.
  return Math.min(base + jitter, remainingBudget);
};

/** Promise-based sleep used between retries. */
export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));
